import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Trophy,
  Swords,
  Crown,
  LayoutDashboard,
  Building2,
  Coins,
  Beaker,
  Search,
  Activity,
  ImagePlus,
  HelpCircle,
  ChevronsLeft,
  ChevronsRight,
  ChevronRight,
  UserCircle2,
  Rocket,
  Users,
  ShieldAlert,
  Shield,
  Compass,
  GitBranch,
  Radar,
  Waypoints,
  Flag,
  CalendarDays,
  MousePointerClick,
  LineChart,
} from 'lucide-react';
import type { Me, Role } from '../lib/api';
import { cn } from '../lib/cn';
import { isAdminRole } from '../lib/roles';
import { isOrgManagerOfAny } from '../lib/orgAccess';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { useViewAs, useEffectiveMe, type ViewAsState } from '../lib/viewAs';
import { CompSwitcher } from './CompSwitcher';
import { RoleBadge } from './RoleBadge';
import { ViewAsSwitcher } from './ViewAsSwitcher';

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar — the spine of the portal.
//
// Role-gated, grouped nav. House language (no borders/glows; space + raised
// fills; accent only on active). Sections are collapsible FOLDERS (file-
// explorer style) on desktop — keeps the rail tight. On mobile, tapping a
// folder opens a bottom-sheet MODAL of that section's options. Width-collapse
// to a 64px icon rail is still available. State persisted in localStorage.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ggm_sidebar_collapsed';
const EXPANDED_KEY = 'ggm_sidebar_expanded';

type NavIcon = typeof Trophy;
export type NavItem = {
  to: string; label: string; icon: NavIcon;
  visibleFor?: (role: Role | undefined) => boolean;
  /** Org-admin de-gate: ALSO show this item when the user is an ORG_ADMIN/
   *  ORG_OWNER of ANY org they belong to — checked against effectiveMe (view-
   *  as-aware: a founder previewing "Org admin (scoped)" sees exactly this
   *  bypass fire for their simulated org; a founder previewing a plain lower
   *  platform role correctly loses it, since the simulated `me` carries no
   *  org memberships — see viewAs.ts's useEffectiveMe), independent of
   *  visibleFor's platform-role check. */
  orgManagerVisible?: boolean;
  /** Pure-platform-ops debug/ops surfaces (AdminPanel, Organizations) gate
   *  internally on the RAW me.role — deliberately not wired to view-as
   *  (2026-07-16 View-As audit): they're founder/platform-tier debug
   *  tooling, not org-scoped surfaces, so deep-wiring effectiveMe into each
   *  wasn't worth it. Cheaper fix: stop OFFERING them in the nav the instant
   *  ANY view-as simulation is active (platform-role or org-admin-scoped) so
   *  a previewing founder can't navigate into the full founder cockpit via
   *  the sidebar. Direct-URL nav still reaches the real page, unchanged —
   *  this only narrows what's offered here.
   *
   *  2026-07-17 org-admin de-gate (discipline+create-match widen):
   *  DisciplinePage and AdminMatchCreate moved OFF this list — they're now
   *  org-manager surfaces (orgManagerVisible below, gated internally via
   *  effectiveMe), so under an org-admin view-as sim they correctly SHOW,
   *  scoped to the simulated org, same as Setup/Clans. */
  hiddenDuringViewAs?: boolean;
};
export type NavGroup = {
  label: string;
  visibleFor: (role: Role | undefined) => boolean;
  /** See NavItem.orgManagerVisible — same "also show for any org manager" rule. */
  orgManagerVisible?: boolean;
  items: NavItem[];
};

// Exported (rather than kept module-private) so it can be exercised as a pure
// data structure in Sidebar.test.ts, matching this repo's preference for
// testing pure functions/data over rendering the component (see
// lib/roles.test.ts, pages/JoinCompScreen.test.ts).
export const GROUPS: NavGroup[] = [
  {
    label: 'Home',
    visibleFor: () => true,
    items: [
      { to: '/', label: 'Command Center', icon: Compass },
      // Top-of-funnel: the self-serve join flow is visible to EVERYONE (incl. the
      // public role) and placed up top, not buried in Run It — a newcomer should
      // be able to find + start the join path. JoinCompScreen gracefully guides
      // whoever can't register yet (build/join a clan first); the stricter
      // "Register my clan" CTAs still use lib/roles.ts's canRegisterClan. (Lisa, 2026-07-18.)
      { to: '/gate/join-comp', label: 'Join a Competition', icon: Flag },
    ],
  },
  {
    label: 'Your Stuff',
    visibleFor: role => role !== 'public',
    items: [
      { to: '/me/card', label: 'My Card', icon: UserCircle2 },
      // Promoted to the sidebar (Lisa, 2026-07-19) — was nav-orphaned, only
      // reachable via the gate's Viewing "Schedule" pill or a match-card
      // link. Reads materialized per-user CALENDAR# rows for the next 14
      // days (pages/Calendar.tsx); the staleness bug (stale scheduledAt
      // baked into the row's sk after a reschedule) is fixed at the write
      // side by shared/calendarSync.ts — this page's read path is untouched.
      { to: '/calendar', label: 'Calendar', icon: CalendarDays },
      { to: '/leader', label: 'My Clan', icon: Crown },
      { to: '/me/org', label: 'My Org', icon: Building2 },
      { to: '/me/comp', label: 'My Comp', icon: Trophy },
      { to: '/how-it-works', label: 'How It Works', icon: HelpCircle },
    ],
  },
  {
    label: 'Overview',
    visibleFor: () => true,
    items: [
      { to: '/standings', label: 'Standings', icon: Trophy },
      { to: '/bracket', label: 'Bracket', icon: GitBranch },
      { to: '/matches', label: 'Matches', icon: Swords },
      { to: '/divisions', label: 'Divisions', icon: LayoutDashboard },
      { to: '/seeding', label: 'Seeding', icon: Trophy },
      // No visibleFor — deliberately shown to EVERY authenticated role,
      // including 'public'. 'public' is the correct, expected resolution for
      // a Discord guild member who hasn't joined a clan yet (backend
      // getRole()'s GSI1 CLAN# scan finds zero rows) — it is NOT a "not
      // entitled" marker. A user with no clan is the ONLY person who NEEDS
      // this page, to find a clan to apply to; someone already in a clan
      // needs it least. The prior `role !== 'public'` gate hid clan
      // discovery from exactly the people who needed it — the same
      // conflation as the sibling BrowseClans.tsx/ClanDetail.tsx apply-gate
      // bug (role !== 'public' misread as "not allowed to apply", when the
      // backend's apply endpoint has no role check at all). Root-caused via
      // the 2026-07-26 JJN test (QATest account: guild member, no clan).
      // This item still only renders inside AuthGate (see App.tsx) — a
      // genuinely logged-out visitor never reaches the Sidebar at all, so
      // omitting visibleFor here does not leak this to the public internet.
      { to: '/clans/browse', label: 'Browse Clans', icon: Search },
    ],
  },
  {
    label: 'Run It',
    visibleFor: role => role === 'leader' || isAdminRole(role),
    // Org-admin de-gate: the GROUP is visible for an org manager of ANY org
    // — the pages themselves (TournamentSetup, ClanManagement) scope down to
    // the SPECIFIC org(s) the caller actually manages.
    orgManagerVisible: true,
    items: [
      { to: '/admin/setup', label: 'Setup', icon: Rocket, visibleFor: role => isAdminRole(role), orgManagerVisible: true },
      { to: '/admin', label: 'Admin', icon: LayoutDashboard, visibleFor: role => isAdminRole(role), hiddenDuringViewAs: true },
      { to: '/admin/discipline', label: 'Discipline', icon: Shield, visibleFor: role => isAdminRole(role), orgManagerVisible: true },
      { to: '/admin/clans', label: 'Clans', icon: Users },
      { to: '/admin/matches/new', label: 'Create Match', icon: Swords, visibleFor: role => isAdminRole(role), orgManagerVisible: true },
      { to: '/orgs', label: 'Organisations', icon: Building2, visibleFor: role => isAdminRole(role), hiddenDuringViewAs: true },
    ],
  },
  {
    label: 'Founder',
    visibleFor: role => role === 'founder',
    items: [
      { to: '/admin/founder', label: 'Founder Admin', icon: Coins },
      { to: '/admin/onboard', label: 'Onboard Client', icon: Rocket },
      { to: '/admin/alerts', label: 'Alerts', icon: ShieldAlert },
      { to: '/admin/org-creation-requests', label: 'Org Requests', icon: Building2 },
      { to: '/admin/match-card-demo', label: 'OCR Lab', icon: Beaker },
      { to: '/admin/user-lookup', label: 'User Lookup', icon: Search },
      { to: '/admin/analytics', label: 'User Analytics', icon: LineChart },
      { to: '/admin/activity', label: 'Activity Log', icon: Activity },
      { to: '/admin/observability', label: 'Observability', icon: Radar },
      { to: '/admin/trace-view', label: 'Trace View', icon: Waypoints },
      { to: '/admin/clickstream', label: 'Clickstream', icon: MousePointerClick },
      { to: '/admin/assets', label: 'Assets', icon: ImagePlus },
    ],
  },
];

function getInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function useIsMobile(): boolean {
  const [m, setM] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setM(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return m;
}

function activeGroupLabel(pathname: string): string | null {
  for (const g of GROUPS) {
    for (const it of g.items) {
      const hit = it.to === '/' ? pathname === '/' : pathname.startsWith(it.to);
      if (hit) return g.label;
    }
  }
  return null;
}

function isItemActive(to: string, pathname: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to);
}

/** Stand-in name for a simulated persona with no org context. Deliberately
 *  not a real-looking name — on a shared screen it must read as synthetic. */
export const SIMULATED_USER_NAME = 'Simulated user';

/**
 * What the pinned bottom user panel shows for identity + credits.
 *
 * WHY THIS EXISTS (2026-08-02 View-As security audit): view-as is driven
 * LIVE in front of customers ("here's what your member sees"), and the panel
 * was rendering the real founder's displayName plus their real token ledger
 * (balance, and "N used / M granted" in a `title` tooltip) straight from the
 * raw `me`, sitting right beside the `sim` badge. Every screen-share demo
 * therefore leaked a real person's name and platform-internal usage figures
 * to an external party — the exact opposite of a clean role simulation.
 *
 * Note this could NOT be fixed by switching to useEffectiveMe: that hook
 * deliberately only rewrites `role` + `orgMemberships` (the gate inputs), and
 * carries displayName/userCredits through untouched. Identity DISPLAY is a
 * separate decision from gate evaluation, so it gets its own one.
 *
 * What an accurate simulation shows instead:
 *   • name — never a real person. The org-scoped sim reuses ViewAsBanner's
 *     exact wording (`Org Admin · <org>`); that org name is the CUSTOMER's
 *     own data, not platform-internal, and it is the genuine context a real
 *     scoped org admin sits in. A plain platform-role sim gets
 *     SIMULATED_USER_NAME — the RoleBadge immediately below already names the
 *     role, so repeating it here would just be noise.
 *   • credits — nothing. A simulated persona has no CREDITS row to read, and
 *     `userCredits: null` is already a real, common state (portalApi's /me
 *     returns null for anyone never granted or charged), so an absent chip is
 *     an honest member view rather than a hole we invented.
 *
 * Pure and exported so the decision can be asserted directly, matching this
 * file's existing preference for testable data over rendering (see GROUPS /
 * Sidebar.test.ts). Mirrors useEffectiveMe's identity guarantee at the
 * display layer: no sim active -> the real values pass through UNCHANGED.
 */
export function userPanelIdentity(
  me: Me,
  viewAs: ViewAsState | null,
): { displayName: string; userCredits: Me['userCredits'] } {
  if (!viewAs) return { displayName: me.displayName, userCredits: me.userCredits };
  return {
    displayName: viewAs.org?.orgName ? `Org Admin · ${viewAs.org.orgName}` : SIMULATED_USER_NAME,
    userCredits: null,
  };
}

export function Sidebar({
  me,
  effectiveRole,
  mobileOpen,
  onMobileClose,
}: {
  me: Me | undefined;
  effectiveRole: Role | undefined;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(getInitialCollapsed);
  const { viewAs, enter, enterOrgAdmin, enterOrgOwner, enterClanLeader, enterClanCaptain } = useViewAs();
  // isFounder gates the ViewAsSwitcher mount itself — this MUST stay on the
  // RAW me, never effectiveMe. If it read effectiveMe, the switcher would
  // vanish the instant a founder started previewing a lower role, trapping
  // them mid-simulation with no way to switch again (only the ViewAsBanner's
  // Exit button would remain).
  const isFounder = me?.role === 'founder';
  // Org-admin de-gate: checked against effectiveMe (view-as-aware) — a
  // founder previewing "Org admin (scoped)" needs this bypass to fire for
  // their simulated org (effectiveMe.orgMemberships carries the sim), while
  // a founder previewing a plain lower platform role must correctly LOSE it
  // (effectiveMe.orgMemberships is empty for a platform-role sim). See
  // viewAs.ts's useEffectiveMe for the identity guarantee: no view-as active
  // -> effectiveMe === me, so this is unchanged for everyone else.
  const effectiveMe = useEffectiveMe(me);
  const isOrgManager = isOrgManagerOfAny(effectiveMe);
  // What the pinned user panel is allowed to SHOW while previewing — see
  // userPanelIdentity above. Not the same question as effectiveMe (gates);
  // this one is about what an external party sees on a screen-share.
  const panelIdentity = me ? userPanelIdentity(me, viewAs) : null;
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const activeGroup = activeGroupLabel(location.pathname);

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(EXPANDED_KEY);
      if (raw) return new Set<string>(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
    return new Set<string>(activeGroup ? [activeGroup] : []);
  });
  const [modalGroup, setModalGroup] = useState<NavGroup | null>(null);

  const toggleExpand = (label: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try { localStorage.setItem(EXPANDED_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const widthClass = collapsed ? 'md:w-[64px]' : 'md:w-60';

  // Publish the sidebar's current width as a root CSS variable so chrome that
  // wants to sit flush against the nav (ToolingShell's attached tool rail —
  // Lisa's "between the navigation bar and the main page" bar, 2026-07-19)
  // can anchor to it and track collapse/expand without prop plumbing.
  useEffect(() => {
    document.documentElement.style.setProperty('--app-sidebar-w', collapsed ? '64px' : '240px');
    return () => { document.documentElement.style.removeProperty('--app-sidebar-w'); };
  }, [collapsed]);

  // The member's org page, rendered IN-SHELL at /org-page/:slug (not the public
  // full-screen /org/:slug route) so it swaps into the content area beside this
  // sidebar like every other nav item. Anyone in the org's Discord server can
  // view it regardless of role, so this link isn't role-gated — it just needs an
  // org to point at. Strip any ORG# prefix ('#' breaks the URL).
  const orgSlug = (me?.orgMemberships?.[0]?.orgId ?? '').replace(/^ORG#/, '');

  const visibleGroups = GROUPS
    .filter(g => g.visibleFor(effectiveRole) || (g.orgManagerVisible && isOrgManager))
    .map(g => {
      let items = g.items.filter(it =>
        ((!it.visibleFor || it.visibleFor(effectiveRole)) || (it.orgManagerVisible && isOrgManager))
        && !(it.hiddenDuringViewAs && viewAs),
      );
      // Surface the org public page under "Overview" for every role that has an org.
      if (g.label === 'Overview' && orgSlug) {
        items = [{ to: `/org-page/${orgSlug}`, label: 'Org Page', icon: Building2 }, ...items];
      }
      return { ...g, items };
    })
    .filter(g => g.items.length > 0);

  return (
    <>
      {/* Mobile drawer scrim — solid, no blur */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={onMobileClose} aria-hidden />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen w-60 flex-col bg-paper-elevated transition-all duration-300',
          'md:sticky',
          widthClass,
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {/* Brand header */}
        <div className="flex items-center justify-between gap-2 px-3 py-5">
          <Link to="/" onClick={onMobileClose} className="flex items-center gap-2 overflow-hidden">
            {collapsed ? (
              <img src="/brand/gganvil-mark.svg" alt="GG Anvil" className="h-9 w-9 shrink-0" />
            ) : (
              <img src="/brand/gganvil-wide.svg" alt="GG Anvil" className="h-9 shrink-0" />
            )}
          </Link>
          {!collapsed && (
            <button
              onClick={toggleCollapsed}
              className={cn('hidden rounded-md p-1.5 text-ink-meta transition-colors hover:bg-white/[0.06] hover:text-ink-primary md:block', MORPH)}
              aria-label="Collapse sidebar"
            >
              <ChevronsLeft className="h-4 w-4" strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Comp switcher */}
        <div className="px-3 pb-4">
          <CompSwitcher collapsed={collapsed} />
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-2">
          {collapsed ? (
            // Icon rail — flat, all items, no folders.
            <div className="space-y-1">
              {visibleGroups.flatMap(g => g.items).map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  onClick={onMobileClose}
                  title={label}
                  className={({ isActive }) =>
                    cn('group flex items-center justify-center rounded-lg px-2.5 py-2 transition-colors', isActive ? 'bg-white/[0.06]' : 'hover:bg-white/[0.035]')
                  }
                >
                  {({ isActive }) => (
                    <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-accent-500' : 'text-ink-meta')} strokeWidth={2} />
                  )}
                </NavLink>
              ))}
            </div>
          ) : (
            // Folder mode — collapsible sections.
            <div className="space-y-1">
              {visibleGroups.map((group, gi) => {
                // Home renders its items as FLAT top-level links (no folder
                // header) — Command Center + Join a Competition sit at the top
                // of the rail. Maps ALL items, not just items[0] (an earlier
                // single-item assumption silently dropped the second link).
                if (group.label === 'Home') {
                  return (
                    <div key={group.label} className={cn('space-y-1', SETTLE)} style={settleDelay(gi)}>
                      {group.items.map(({ to, label, icon: Icon }, ii) => (
                        <NavLink
                          key={to}
                          to={to}
                          end={to === '/'}
                          onClick={onMobileClose}
                          className={({ isActive }) =>
                            cn(
                              'group flex items-center gap-3 rounded-lg px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.16em] transition',
                              isActive ? 'bg-white/[0.06] text-ink-primary' : 'text-ink-secondary hover:translate-x-0.5 hover:bg-white/[0.035] hover:text-ink-primary',
                              SETTLE
                            )
                          }
                          style={settleDelay(ii)}
                        >
                          {({ isActive }) => (
                            <>
                              <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-accent-500' : 'text-ink-meta group-hover:text-ink-secondary')} strokeWidth={2} />
                              {label}
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  );
                }

                const isExp = expanded.has(group.label);
                const groupActive = activeGroup === group.label;
                return (
                  <div key={group.label} className={SETTLE} style={settleDelay(gi)}>
                    <button
                      onClick={() => (isMobile ? setModalGroup(group) : toggleExpand(group.label))}
                      className={cn('group flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-white/[0.035]', MORPH)}
                      aria-expanded={!isMobile && isExp}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-display text-[10px] font-black uppercase tracking-[0.24em] text-ink-meta group-hover:text-ink-secondary">
                          {group.label}
                        </span>
                        {/* a dot marks the section holding the active page when it's closed */}
                        {groupActive && (isMobile || !isExp) && <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />}
                      </span>
                      <ChevronRight
                        className={cn('h-3.5 w-3.5 shrink-0 text-ink-meta transition-transform', !isMobile && isExp && 'rotate-90')}
                        strokeWidth={2.5}
                      />
                    </button>

                    {!isMobile && isExp && (
                      <div className="mb-1 mt-0.5 space-y-0.5">
                        {group.items.map(({ to, label, icon: Icon }, ii) => (
                          <NavLink
                            key={to}
                            to={to}
                            end={to === '/'}
                            onClick={onMobileClose}
                            className={({ isActive }) =>
                              cn(
                                'group flex items-center gap-3 rounded-lg py-2 pl-5 pr-3 font-display text-[11px] font-black uppercase tracking-[0.16em] transition',
                                isActive ? 'bg-white/[0.06] text-ink-primary' : 'text-ink-secondary hover:translate-x-0.5 hover:bg-white/[0.035] hover:text-ink-primary',
                                SETTLE
                              )
                            }
                            style={settleDelay(ii)}
                          >
                            {({ isActive }) => (
                              <>
                                <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-accent-500' : 'text-ink-meta group-hover:text-ink-secondary')} strokeWidth={2} />
                                <span>{label}</span>
                              </>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </nav>

        {/* Collapse button (when collapsed, below nav) */}
        {collapsed && (
          <button
            onClick={toggleCollapsed}
            className={cn('mx-auto mb-2 hidden rounded-md p-1.5 text-ink-meta transition-colors hover:bg-white/[0.06] hover:text-ink-primary md:block', MORPH)}
            aria-label="Expand sidebar"
          >
            <ChevronsRight className="h-4 w-4" strokeWidth={2.5} />
          </button>
        )}

        {/* User panel — pinned bottom */}
        {me && panelIdentity && (
          <div className="px-3 py-3">
            {collapsed ? (
              <div className="flex flex-col items-center gap-2">
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-md bg-white/[0.06] font-display text-sm font-black text-ink-primary"
                  title={panelIdentity.displayName}
                >
                  {panelIdentity.displayName[0]?.toUpperCase() ?? '?'}
                </div>
                {panelIdentity.userCredits && (
                  <span className="font-mono text-[9px] font-bold text-ink-meta" title={`Tokens · ${panelIdentity.userCredits.totalUsed} used / ${panelIdentity.userCredits.totalGranted} granted`}>
                    {panelIdentity.userCredits.balance}
                  </span>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-xs font-black uppercase tracking-wider text-ink-primary">
                      {panelIdentity.displayName}
                    </p>
                    <div className="mt-1 flex items-center gap-1.5">
                      {effectiveRole && (
                        <RoleBadge role={effectiveRole} size="xs" />
                      )}
                      {viewAs && (
                        <span className="font-mono text-[8px] uppercase tracking-wider text-red-300/80">
                          sim
                        </span>
                      )}
                    </div>
                  </div>
                  {panelIdentity.userCredits && (
                    <div
                      className="flex items-center gap-1 rounded-md bg-white/[0.06] px-2 py-1 font-mono text-[10px] font-bold text-ink-secondary"
                      title={`Your tokens · ${panelIdentity.userCredits.totalUsed} used / ${panelIdentity.userCredits.totalGranted} granted`}
                    >
                      <Coins className="h-2.5 w-2.5 text-accent-500" strokeWidth={3} />
                      {panelIdentity.userCredits.balance}
                    </div>
                  )}
                </div>
                {isFounder && (
                  <ViewAsSwitcher
                    current={viewAs}
                    onEnter={enter}
                    onEnterOrgAdmin={enterOrgAdmin}
                    onEnterOrgOwner={enterOrgOwner}
                    onEnterClanLeader={enterClanLeader}
                    onEnterClanCaptain={enterClanCaptain}
                  />
                )}
                <div className="mt-2 flex gap-3">
                  <NavLink to="/privacy" className="text-[9px] text-ink-meta hover:text-ink-secondary transition-colors">Privacy</NavLink>
                  <NavLink to="/terms" className="text-[9px] text-ink-meta hover:text-ink-secondary transition-colors">Terms</NavLink>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Mobile section sheet — tapping a folder header opens its options here. */}
      {isMobile && modalGroup && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/60 md:hidden" onClick={() => setModalGroup(null)} aria-hidden />
          <div className="sheet-up fixed inset-x-0 bottom-0 z-[70] rounded-t-2xl bg-paper-panel p-4 pb-8 md:hidden">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
            <p className="mb-3 px-1 font-display text-[10px] font-black uppercase tracking-[0.24em] text-ink-meta">
              {modalGroup.label}
            </p>
            <div className="space-y-1.5">
              {modalGroup.items.map(({ to, label, icon: Icon }) => {
                const active = isItemActive(to, location.pathname);
                return (
                  <button
                    key={to}
                    onClick={() => { setModalGroup(null); onMobileClose(); navigate(to); }}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left font-display text-[12px] font-black uppercase tracking-[0.14em] transition-colors',
                      active ? 'bg-white/[0.08] text-ink-primary' : 'bg-white/[0.04] text-ink-secondary hover:bg-white/[0.07] hover:text-ink-primary'
                    )}
                  >
                    <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-accent-500' : 'text-ink-meta')} strokeWidth={2} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
